import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  NJButton,
  NJCard,
  NJHeading,
  NJIcon,
  NJInlineMessage,
  NJInputSearch,
  NJPagination,
  NJSpinner,
  NJTag,
  NJText,
} from "@engie-group/fluid-design-system-react";
import { FilterChip } from "@/components/store/FilterChip";
import { MetaTagList } from "@/components/store/MetaTagList";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { Agent } from "@/types";
import { useCreateSession } from "@/features/sessions/useSessions";
import { useCancelAccessRequest, useRequestAccess } from "./useAccessRequests";
import {
  type AgentAccessFilter,
  useAgents,
  useAgentUseCases,
} from "./useAgents";

const PAGE_SIZE = 12;

const ACCESS_FILTERS: { label: string; value: AgentAccessFilter }[] = [
  { label: "All", value: "all" },
  { label: "Granted", value: "granted" },
  { label: "Pending", value: "pending" },
  { label: "Restricted", value: "restricted" },
];

const ACCESS_LABELS: Record<string, string> = {
  granted: "Granted",
  pending: "Pending",
  restricted: "Restricted",
};

function accessTagVariant(access: string): "green" | "orange" | "grey" {
  if (access === "granted") return "green";
  if (access === "pending") return "orange";
  return "grey";
}

function AgentCard({ agent }: { agent: Agent }) {
  const navigate = useNavigate();
  const requestAccess = useRequestAccess();
  const cancelAccessRequest = useCancelAccessRequest();
  const createSession = useCreateSession();

  return (
    <NJCard
      onClick={() => navigate(`/agents/${agent.slug}`)}
      className="!mb-0 flex cursor-pointer flex-col gap-3 p-5 shadow-sm transition hover:border-nj-brand-moderate-default hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <NJHeading as="h2" scale="md">
          {agent.name}
        </NJHeading>
        <NJTag
          label={ACCESS_LABELS[agent.access] ?? agent.access}
          variant={accessTagVariant(agent.access)}
        />
      </div>

      {(agent.useCases ?? []).length > 0 && (
        <MetaTagList label="Use cases" items={agent.useCases ?? []} max={3} />
      )}

      <NJText
        as="p"
        scale="sm"
        className="min-h-[48px] text-nj-neutral-secondary-default"
      >
        {agent.description}
      </NJText>

      <div className="mt-auto flex items-end justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <NJText
            as="span"
            scale="xs"
            className="text-nj-neutral-secondary-default"
          >
            Maintained by <strong>{agent.maintainer}</strong>
          </NJText>
          {agent.createdAt && (
            <NJText
              as="span"
              scale="xs"
              className="text-nj-neutral-secondary-default"
            >
              Added {new Date(agent.createdAt).toLocaleDateString("en-GB")}
            </NJText>
          )}
        </div>
        <div onClick={(event) => event.stopPropagation()}>
          {agent.access === "granted" && (
            <NJButton
              label="Start session"
              icon="open_in_new"
              emphasis="subtle"
              size="small"
              disabled={createSession.isPending}
              onClick={() =>
                createSession.mutate(agent.slug, {
                  onSuccess: (session) => navigate(`/chat/${session.id}`),
                })
              }
            />
          )}
          {agent.access === "restricted" && (
            <NJButton
              label="Request access"
              emphasis="subtle"
              size="small"
              disabled={requestAccess.isPending}
              onClick={() => requestAccess.mutate(agent.slug)}
            />
          )}
          {agent.access === "pending" && (
            <NJButton
              label="Cancel request"
              emphasis="subtle"
              size="small"
              disabled={cancelAccessRequest.isPending}
              onClick={() => cancelAccessRequest.mutate(agent.slug)}
            />
          )}
        </div>
      </div>
    </NJCard>
  );
}

/** Agent Lib — server-side search, status/use-case filters and pagination. */
export function AgentCataloguePage() {
  const [searchInput, setSearchInput] = useState("");
  const [access, setAccess] = useState<AgentAccessFilter>("all");
  const [useCase, setUseCase] = useState<string>("all");
  const [page, setPage] = useState(1);

  const query = useDebouncedValue(searchInput);

  useEffect(() => setPage(1), [query, access, useCase]);

  const { data, isLoading, isError } = useAgents({
    q: query,
    access,
    useCase: useCase === "all" ? undefined : useCase,
    skip: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  });
  const { data: useCases } = useAgentUseCases();

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1),
    [data],
  );

  return (
    <div className="flex w-full flex-col">
      <header className="flex flex-col gap-5 border-b border-nj-neutral-subtle-default pb-5">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-nj-brand-secondary-default text-nj-brand-solid-default">
            <NJIcon name="storefront" />
          </span>
          <div className="flex flex-col">
            <NJHeading as="h1" scale="lg">
              Agent Lib
            </NJHeading>
            <NJText
              as="p"
              scale="sm"
              className="text-nj-neutral-secondary-default"
            >
              Discover active agents and their access status.
            </NJText>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <NJInputSearch
            label="Search agents"
            placeholder="Search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.currentTarget.value)}
          />

          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <NJText
                as="span"
                scale="sm"
                className="min-w-[118px] font-nj-bold"
              >
                Status
              </NJText>
              <div className="flex flex-wrap gap-2">
                {ACCESS_FILTERS.map((filter) => (
                  <FilterChip
                    key={filter.value}
                    active={access === filter.value}
                    label={filter.label}
                    onClick={() => setAccess(filter.value)}
                  />
                ))}
              </div>
            </div>

            {useCases && useCases.length > 0 && (
              <div className="flex flex-wrap items-center gap-3">
                <NJText
                  as="span"
                  scale="sm"
                  className="min-w-[118px] font-nj-bold"
                >
                  Filter by use case
                </NJText>
                <div className="flex flex-wrap gap-2">
                  <FilterChip
                    active={useCase === "all"}
                    label="All"
                    onClick={() => setUseCase("all")}
                  />
                  {useCases.map((value) => (
                    <FilterChip
                      key={value}
                      active={useCase === value}
                      label={value}
                      onClick={() => setUseCase(value)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <section className="pt-5">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <NJSpinner aria-label="Loading the agent catalogue" />
          </div>
        ) : isError ? (
          <NJInlineMessage variant="error">
            Could not load the agent catalogue.
          </NJInlineMessage>
        ) : (
          <>
            <div className="mx-auto grid max-w-[1240px] gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(320px,1fr))]">
              {(data?.items ?? []).map((agent) => (
                <AgentCard key={agent.slug} agent={agent} />
              ))}
            </div>

            {data && data.items.length === 0 && (
              <NJText
                as="p"
                scale="sm"
                className="mx-auto max-w-[1240px] text-nj-neutral-secondary-default"
              >
                No agents match this search.
              </NJText>
            )}

            {totalPages > 1 && (
              <div className="mt-[18px] flex justify-center">
                <NJPagination
                  count={totalPages}
                  currentItem={page}
                  ariaLabelPrevious="Previous agents page"
                  ariaLabelNext="Next agents page"
                  onChange={(item: number) => setPage(item)}
                />
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
